import React, { useEffect, useRef, useState } from "react";
import "./styles.scss";
import { InfoPopup, useInfoPopup } from "../InfoPopup/InfoPopup";
import { ITextsToTranslate } from "../../interfaces";
import { buildInstallPostbackUrl } from "../../utils/buildInstallPostbackUrl";
import ShareColorIcon from "../../assets/icons/ShareColorIcon";
import FavoriteIcon from "../../assets/icons/FavoriteIcon";

interface InstallButtonProps {
  translations: ITextsToTranslate;
  defferedPrompt: any;
  setDefferedPrompt: any;
  testPwaDomain?: string;
  pwaData: any;
  currentGeo?: string;
  ip?: string;
  onInstallCallback?: () => void;
  xRequestedWith?: string;
}

function testIsInAppBrowser(xRequestedWith = "") {
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  if (!isAndroid) return false;
  const knownInAppPatterns =
    /FBAN|FBAV|Instagram|Line|Twitter|Telegram|TikTok|Snapchat|Pinterest|LinkedIn|WhatsApp|Messenger/i;
  const isKnownInApp = knownInAppPatterns.test(ua);

  const isLikelyWebView = navigator.vendor === "" && document.referrer === "";
  const isNotStandalone = !window.matchMedia("(display-mode: standalone)").matches;

  const xrw = (xRequestedWith || "").toLowerCase();
  const isFromAppHeader = [
    "org.telegram.messenger",
    "com.instagram.android",
    "com.facebook.katana",
    "com.twitter.android",
    "com.zhiliaoapp.musically", // TikTok
    "com.snapchat.android",
    "com.pinterest",
    "com.linkedin.android",
    "com.whatsapp",
  ].some((appId) => xrw === appId);

  return isKnownInApp || isFromAppHeader || (isLikelyWebView && isNotStandalone);
}

function isIOS() {
  //@ts-ignore
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

const InstallButton: React.FC<InstallButtonProps> = ({
  translations,
  defferedPrompt,
  setDefferedPrompt,
  testPwaDomain,
  pwaData,
  currentGeo,
  onInstallCallback,
  xRequestedWith,
}) => {
  const [loading, setLoading] = useState(false);
  const domain = window.location.hostname;
  const installButton = useRef(null);
  const isInstalledStorage = localStorage.getItem("pwaLastInstall");
  const storagePWAData = getCookie("pwaData");
  const [isInstalled, setIsInstalled] = useState(Boolean(isInstalledStorage || storagePWAData));
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const isNegativeWallet = pwaData?.wallet <= 0;
  const { openPopup, closePopup, handleOpenPopup } = useInfoPopup();

  useEffect(() => {
    setIsInAppBrowser(testIsInAppBrowser(xRequestedWith));
  }, []);

  const saveInstall = async () => {
    const requestDomain = testPwaDomain || domain;
    if (requestDomain) {
      try {
        const response = await fetch(`https://${requestDomain}/api/v1/pwa/current/install?geo=${currentGeo || ""}`);
        if (!response.ok) {
          throw new Error("Ошибка при загрузке данных");
        }

        const installPostbackTemplate = pwaData?.installPostback.url;

        if (installPostbackTemplate) {
          const finalInstallPostbackUrl = buildInstallPostbackUrl(installPostbackTemplate);

          if (finalInstallPostbackUrl) {
            const method = pwaData?.installPostback.method;

            const options: RequestInit = {
              method,
              headers: {},
            };

            if (method === "POST") {
              options.headers = {
                "Content-Type": "application/json",
              };
              options.body = JSON.stringify({});
            }

            await fetch(finalInstallPostbackUrl, options);
          }
        }
      } catch (error) {
        console.error("error", error);
        setLoading(false);
      }
    }
  };

  const continueToInstall = async () => {
    onInstallCallback && onInstallCallback();
    const fbq = (window as any).fbq || function () {};
    fbq("track", "Lead", {
      value: 1,
      content_name: "install",
      content_category: "pwa",
      content_ids: pwaData.appName,
      content_type: "pwa",
      content_ids_source: "pwa",
      content_ids_source_url: pwaData.offerLink,
      content_ids_source_domain: domain,
    });
    await saveInstall();
    setCookie("pwaData", JSON.stringify(pwaData), 2);

    setTimeout(() => {
      localStorage.setItem("pwaLastInstall", String(Date.now()));
      setIsInstalled(true);
      setLoading(false);
      // window.location.reload();
    }, 2000);
  };

  const handleClick = () => {
    setLoading(true);

    const combinedQueryString = mergeQueryStrings(pwaData.offerLink, window.location.search);
    const finalLink = `${pwaData?.blackLink}${combinedQueryString.length ? "?" + combinedQueryString : ""}`;

    if (isInstalled) {
      localStorage.setItem("finalLink", finalLink);
      window.location.href = decodeURI(finalLink);
      return;
    }

    if (isNegativeWallet) {
      setLoading(false);
      return;
    }
    setTimeout(() => {
      setLoading(false);
      if (isIOS()) {
        void continueToInstall();
        return;
      }
      if (defferedPrompt) {
        try {
          defferedPrompt.prompt();
          defferedPrompt.userChoice
            .then((choiceResult: any) => {
              if (choiceResult.outcome === "accepted") {
                setLoading(true);
                void continueToInstall();
              } else {
                localStorage.clear();
                sessionStorage.clear();
                document.cookie.split(";").forEach((cookie) => {
                  const name = cookie.split("=")[0].trim();
                  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
                });
                window.location.reload();
              }
              console.log(2);
              setDefferedPrompt(null);
            })
            .catch((err: any) => {
              console.log(3);
              console.log(err);
              handleOpenPopup("Error install");
              setDefferedPrompt(null);
            });
        } catch (error) {
          console.error("Error:", error);
        }
      } else {
        void continueToInstall();
        setIsInstalled(true);
      }
    }, 2000);
  };

  return (
    <div className="install-button-container">
      <InfoPopup open={openPopup} onClose={closePopup} openPopupText="" />
      <div className="install-button-web">
        {isInAppBrowser ? (
          <a
            className={`color-button-web ${pwaData.secondPwaTheme ? "color-button-web-second-theme" : ""}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            href={
              "intent://" +
              window.location.href.replace(/^https?:\/\//, "") +
              "#Intent;scheme=https;package=com.android.chrome;end"
            }
          >
            {translations.install}
          </a>
        ) : (
          <button
            className={`color-button-web ${loading ? "loading" : ""} ${
              pwaData?.secondPwaTheme ? "color-button-web-second-theme" : ""
            }`}
            onClick={handleClick}
            disabled={loading}
            ref={installButton}
          >
            {loading ? (
              <div className="circular-progress invert" />
            ) : isInstalled ? (
              translations.openBtn
            ) : (
              translations.install
            )}
          </button>
        )}

        <div className="sub-buttons-container">
          <div className="sub-button-wrapper">
            <ShareColorIcon isSecondThemeColor={pwaData?.secondPwaTheme} />
            <p className={`share-text ${pwaData?.secondPwaTheme ? "share-text-second-theme" : ""}`}>Share</p>
          </div>
          <div className="sub-button-wrapper">
            <FavoriteIcon isSecondThemeColor={pwaData?.secondPwaTheme} />
            <p className={`favorite-text ${pwaData?.secondPwaTheme ? "favorite-text-second-theme" : ""}`}>
              Add to wishlist
            </p>
          </div>
        </div>
      </div>
      {loading && (
        <div className="backdrop">
          <div className="circular-progress" />
        </div>
      )}
    </div>
  );
};

function getCookie(name: string) {
  const cookies = document.cookie.split("; ");
  for (let i = 0; i < cookies.length; i++) {
    const [cookieName, cookieValue] = cookies[i].split("=");
    try {
      if (cookieName === name) {
        return cookieValue;
      }
    } catch (e) {
      console.warn("Error decoding cookie:", e);
    }
  }
  return null; // Return null if the cookie is not found
}

const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=/";
};

function parseQueryString(queryString: string) {
  const query = queryString.includes("?") ? queryString.split("?")[1] : queryString;
  const params = new URLSearchParams(query);
  const result: { [key: string]: string } = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}

function mergeQueryStrings(queryString1: string, queryString2: string) {
  const params1 = parseQueryString(queryString1);
  const params2 = parseQueryString(queryString2);

  const mergedParams = { ...params1, ...params2 };

  const urlSearchParams = new URLSearchParams(mergedParams);
  return urlSearchParams.toString();
}

export default InstallButton;
